import React, { useEffect, useState } from 'react';
import { Switch } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getWorkspaceCounts } from '../lib/workspaceActions';

const formatCountLabel = (count, singular) =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

/**
 * Duplicate workspace options + progress UI.
 * Controlled: open via isOpen; parent owns confirm/cancel and progress state.
 */
const DuplicateWorkspaceModal = ({
  isOpen,
  workspace,
  includeBaskets,
  onIncludeBasketsChange,
  isDuplicating,
  duplicateProgress,
  duplicateStatus,
  onCancel,
  onConfirm,
}) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!isOpen || !workspace?.id) {
      setCounts(null);
      return undefined;
    }

    setCounts(null);
    getWorkspaceCounts(workspace.id)
      .then((result) => {
        if (!cancelled) setCounts(result);
      })
      .catch((error) => {
        console.error('Error loading workspace counts:', error);
        if (!cancelled) setCounts(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, workspace?.id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-md w-full">
        <div className="px-6 py-4 border-b-2 border-neutral-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Duplicate Workspace</h3>
            <p className="text-sm text-neutral-600 mt-1">
              {workspace?.name}
            </p>
            {counts && (
              <p className="text-xs text-neutral-500 mt-0.5">
                {formatCountLabel(counts.basketCount, 'basket')} · {formatCountLabel(counts.topicCount, 'topic')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isDuplicating}
            className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5 text-neutral-600" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium text-neutral-900">Include baskets</div>
                <div className="text-xs text-neutral-600">
                  {includeBaskets
                    ? 'Premade baskets with topics inside'
                    : 'No baskets - create your own basket structure'}
                </div>
              </div>
              <Switch
                checked={includeBaskets}
                onChange={onIncludeBasketsChange}
                disabled={isDuplicating}
                className={`${
                  includeBaskets ? 'bg-emerald-600' : 'bg-neutral-200'
                } relative inline-flex h-6 w-11 items-center rounded-full border-2 border-neutral-900 transition-colors focus:outline-none`}
              >
                <span className={`${
                  includeBaskets ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
              </Switch>
            </div>

            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-1">
                <div className="text-sm font-medium text-neutral-900">Include items</div>
                <div className="text-xs text-neutral-600">All topics will be copied</div>
              </div>
              <Switch
                checked={true}
                disabled={true}
                className="bg-emerald-600 relative inline-flex h-6 w-11 items-center rounded-full border-2 border-neutral-900 transition-colors focus:outline-none"
              >
                <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition-transform" />
              </Switch>
            </div>
          </div>

          {isDuplicating && (
            <div className="space-y-2 pt-2">
              <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cranberry-500 transition-all duration-300 ease-out"
                  style={{ width: `${duplicateProgress}%` }}
                />
              </div>
              <p className="text-sm text-neutral-600 text-center">
                {duplicateStatus}
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-neutral-900 hover:text-cranberry-700 transition-colors"
            disabled={isDuplicating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDuplicating}
            className="px-5 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWorkspaceModal;
